import { Link } from "react-router-dom";
import Block from "../components/Block";
import Seam from "../components/Seam";
import Seo from "../components/Seo";
import { SITE } from "../seo/siteConfig";

const DEMO = "mailto:vivek@gapsmiths.com?subject=Prism%20Books%20demo";
const TALK = "mailto:vivek@gapsmiths.com?subject=Prism%20Books";

const prismJsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Prism Books",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  url: `${SITE.url}/prism`,
  description:
    "Finance intelligence and automation for businesses running on Tally: WhatsApp-to-Tally payables, plain-English questions over your books, multi-company consolidation and Schedule III reporting.",
  publisher: { "@type": "Organization", name: SITE.name },
};

const faqs = [
  {
    q: "What is Prism Books?",
    a: [
      "Prism Books is a finance intelligence and automation platform built around the Tally accounting ecosystem. It sits alongside Tally rather than replacing it, and does three things: captures payables automatically, answers questions about the books in plain English, and produces consolidated group reporting including Schedule III statements.",
    ],
  },
  {
    q: "Do I have to move off Tally?",
    a: [
      "No, and that is the point. Tally stays your book of record. Prism Books reads from it and, where you approve it, writes back into it. Your accountant keeps working the way they already work; the manual assembly around them goes away.",
    ],
  },
  {
    q: "How does WhatsApp-to-Tally accounts payable work?",
    a: [
      "Someone in the field photographs a bill and sends it on WhatsApp. Prism Books reads the document — vendor, amount, tax, date, line items — matches it against existing masters, and prepares the voucher. Once it is approved it posts into Tally under the right ledger.",
      "The people who create the paperwork stop being a bottleneck, because they no longer have to reach a desk to file anything.",
    ],
  },
  {
    q: "What is “Ask the Books”?",
    a: [
      "A natural-language query layer over your accounting data. “What is outstanding from this customer over 90 days?”, “how much did we spend on freight this quarter versus last?”, “show me expenses above 1 lakh not yet approved” — typed as a question, answered with the figure and the vouchers behind it.",
      "It removes the step where someone exports a day book and pivots it to answer a two-minute question.",
    ],
  },
  {
    q: "Can it consolidate multiple companies?",
    a: [
      "Yes. Multi-company consolidation is built in: group entities are mapped once, then consolidated reporting runs on demand rather than being rebuilt in a spreadsheet each period. Inter-company entries, differing chart-of-accounts structures and multiple Tally instances are handled as part of the mapping.",
    ],
  },
  {
    q: "Does it produce Schedule III reporting?",
    a: [
      "Yes. Prism Books produces Schedule III–format financial statements from your Tally data, with the grouping and disclosure structure the format requires, so the statutory pack does not have to be assembled by hand every year.",
    ],
  },
  {
    q: "Who is it for?",
    a: [
      "Finance controllers and CFOs at mid-market businesses, group companies running several entities on Tally, accounting and CA firms serving multiple clients, and any business where bills arrive from the field faster than the accounts team can key them in.",
    ],
  },
];

export default function Prism() {
  return (
    <>
      <Seo
        title="Prism Books — finance intelligence and automation for Tally"
        description="Prism Books: WhatsApp-to-Tally payables, plain-English questions over your books, multi-company consolidation and Schedule III reporting. A Gapsmiths product."
        jsonLd={prismJsonLd}
      />
      <section className="wrap page-head">
        <p className="crumb">
          <Link to="/our-ip">Our IP &amp; products</Link> / Prism Books
        </p>
        <h1>Your books, answered in one line</h1>
        <p className="lede">
          Finance intelligence and automation for businesses running on Tally.
          Payables captured from WhatsApp, questions answered in plain English, group
          companies consolidated, Schedule III produced.
        </p>
        <div className="actions">
          <a className="btn btn-solid" href={DEMO}>Request a demo</a>
          <a className="btn btn-ghost" href={TALK}>Talk to the team</a>
        </div>
      </section>

      <Seam />

      <Block marker="Prism Books at a glance">
        <dl className="rows">
          <div className="row"><dt>Product</dt><dd>Prism Books — finance intelligence and automation for the Tally ecosystem</dd></div>
          <div className="row"><dt>Relationship to Tally</dt><dd>Sits alongside Tally. Tally stays the book of record; Prism Books reads it and writes approved entries back.</dd></div>
          <div className="row"><dt>Core capabilities</dt><dd>WhatsApp-to-Tally accounts-payable automation, "Ask the Books" natural-language querying, multi-company consolidation, Schedule III reporting</dd></div>
          <div className="row"><dt>Best for</dt><dd>Finance controllers, CFOs, group companies on multiple Tally instances, CA and accounting firms</dd></div>
          <div className="row"><dt>Removes</dt><dd>Manual voucher entry from photographed bills, spreadsheet consolidation, ad-hoc export-and-pivot reporting</dd></div>
          <div className="row"><dt>Controls</dt><dd>Approval before posting, full audit trail from voucher back to source document, scoped permissions</dd></div>
          <div className="row"><dt>Region</dt><dd>Built for Indian accounting practice, including Schedule III presentation requirements</dd></div>
          <div className="row"><dt>Start here</dt><dd><a href={DEMO}>Request a demo</a></dd></div>
        </dl>
      </Block>

      <Seam />

      <Block marker="The problem">
        <h2>The books are accurate. Getting anything out of them is the work.</h2>
        <p>
          Bills arrive as photographs. Someone keys them in. Month end arrives and
          three entities get exported into a workbook that gets rebuilt, slightly
          differently, every period. A director asks a one-line question and it costs
          an afternoon.
        </p>
        <p>
          None of that is an accounting problem. It is an assembly problem — and
          assembly is exactly what software should be doing.
        </p>
        <ul className="pains">
          <li><strong>Payables lag reality.</strong> The expense exists days before the voucher does.</li>
          <li><strong>Consolidation is manual.</strong> Group reporting lives in a workbook only one person fully understands.</li>
          <li><strong>Questions queue.</strong> Every ad-hoc figure needs the accountant to stop and produce it.</li>
          <li><strong>Statutory formats are rebuilt annually</strong> instead of generated.</li>
        </ul>
      </Block>

      <Seam />

      <Block marker="Features" bodyClassName="wide">
        <h2>What Prism Books does</h2>
        <div className="features">
          <div className="feature"><h3>WhatsApp AP capture</h3><p>Photograph a bill, send it, and it is read, matched to masters and prepared as a voucher for approval.</p></div>
          <div className="feature"><h3>Posts into Tally</h3><p>Approved entries write back into Tally under the correct ledger, with the source document attached to the trail.</p></div>
          <div className="feature"><h3>Ask the Books</h3><p>Natural-language questions over ledgers, vouchers and masters, answered with the figure and the entries behind it.</p></div>
          <div className="feature"><h3>Multi-company consolidation</h3><p>Map group entities once; run consolidated reporting on demand instead of rebuilding a workbook each period.</p></div>
          <div className="feature"><h3>Schedule III reporting</h3><p>Statutory-format statements generated from your own data, with the required grouping and disclosure structure.</p></div>
          <div className="feature"><h3>Receivables and ageing</h3><p>Outstanding by customer, bucket and age, live, without an export.</p></div>
          <div className="feature"><h3>MIS on a schedule</h3><p>The monthly pack produced and delivered rather than assembled the night before the review.</p></div>
          <div className="feature"><h3>Approval workflow</h3><p>Nothing posts without the approval you configure. Every automated action is logged and reversible.</p></div>
          <div className="feature"><h3>Audit trail</h3><p>Every voucher traces back to the document that created it — useful in an audit, essential in a dispute.</p></div>
        </div>
      </Block>

      <Seam />

      <Block marker="Questions the books can answer">
        <h2>Typed, not exported</h2>
        <ul className="queries">
          <li>What is outstanding from this customer beyond 90 days?</li>
          <li>How much did we spend on freight this quarter versus last?</li>
          <li>Show me expenses above one lakh awaiting approval.</li>
          <li>What is the consolidated revenue across all group entities this month?</li>
          <li>Which vendors have unmatched bills this period?</li>
          <li>What is our creditor ageing by bucket?</li>
          <li>Which cost head moved most against last year?</li>
          <li>Produce the Schedule III balance sheet for the year.</li>
        </ul>
      </Block>

      <Seam />

      <Block marker="Questions">
        <h2>Prism Books FAQ</h2>
        <div className="faqs">
          {faqs.map((item) => (
            <details className="faq" key={item.q}>
              <summary>{item.q}</summary>
              {item.a.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </details>
          ))}
          <details className="faq">
            <summary>Is our financial data safe?</summary>
            <p>
              Access is scoped and permissioned, write-backs into Tally are explicit
              and logged, and every automated posting keeps an audit trail linking the
              voucher to the original document. Financial data is never pooled across
              customers or used to train shared models. See the{" "}
              <Link to="/privacy">privacy policy</Link>.
            </p>
          </details>
          <details className="faq">
            <summary>How do we get started?</summary>
            <p>
              Prism Books is set up with our team, because the mapping to your Tally
              structure matters. <a href={DEMO}>Request a demo</a> and we will walk
              through your ledger structure and what automation would actually save.
            </p>
          </details>
        </div>
      </Block>

      <Seam />

      <Block marker="Next">
        <h2>See it against your own ledgers</h2>
        <p>
          A demo runs on your Tally structure, not a sample company, so you can see
          exactly what would be automated.
        </p>
        <div className="actions">
          <a className="btn btn-solid" href={DEMO}>Request a demo</a>
          <a className="btn btn-ghost" href={TALK}>Contact the team</a>
        </div>
      </Block>
    </>
  );
}
